import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import GridItem from "@insite/mobius/GridItem";
import Grid from "@insite/mobius/Icons/Grid";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface OrderDateStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    orderDate?: TypographyProps;
    warningSectionCustom?: InjectableCss;
    warningTextCustom?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => ({
    currentBillTo: getBillToState(state, getSession(state).billToId).value,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export const orderDateStyles: OrderDateStyles = {
    wrapper: {
        css: css`
            padding-bottom: 10px;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
            font-size: 16px;
        `,
    },
    warningSectionCustom: {
        css: css`
            display: inline;
            border: 2px solid #f0b003;
            padding: 10px 10px;
            margin: 15px 0px;
            border-radius: 3px;
            .errorList {
                list-style-type: disc;
                margin-left: 1rem;
                li {
                    color: #f0b003;
                }
            }
        `,
    },
    warningTextCustom: {
        css: css`
            font-size: 15px;
            color: #f0b003;
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
};
const styles = orderDateStyles;
const StyledSection = getStyledWrapper("section");
const OrderDate: FC<Props> = ({ currentBillTo }: Props) => {
    const { value: order } = useContext(OrderStateContext);

    const isWorEnable = currentBillTo?.properties?.isWOREnable?.toLowerCase() === "true";
    const currentBillCustomerNumber = currentBillTo?.customerNumber;
    const orderBillCustomerNumber = order?.customerNumber;
    const isSameBillCustomerOrder = currentBillCustomerNumber === orderBillCustomerNumber;
    if (!order || !order.orderDate) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.titleText} id="orderDate">
                {translate("Order Date")}
            </Typography>
            <Typography {...styles.orderDate} aria-labelledby="orderDate" data-test-selector="orderDate">
                <LocalizedDateTime
                    dateTime={order.orderDate}
                    options={{ year: "numeric", month: "numeric", day: "numeric" }}
                />
            </Typography>
            {isWorEnable && isSameBillCustomerOrder && order.properties?.kgpOrderHold === "Yes" && (
                <GridItem width={12}>
                    <StyledSection {...styles.warningSectionCustom}>
                        <Typography {...styles.warningTextCustom}>{siteMessage("processingDelay")}</Typography>
                    </StyledSection>
                </GridItem>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderDate),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
